import { Button, Loader, Slider } from "@calcom/ui";
import { trpc } from "@calcom/trpc";
import { FiSearch } from "@calcom/ui/components/icon";
import { useLocale } from "@calcom/lib/hooks/useLocale";
import cn from "@calcom/lib/classNames";
import { useMemo, useState } from "react";
import { MeetingBookingModal } from "@components/booking/dialog/MeetinBookingModal";
import { useRouter } from "next/router";
import { useSession } from "next-auth/react";

const VISIBLE_COACHES = 3;

export const CoachStep = (props: { isEmbed?: boolean }) => {
  const isEmbed = props.isEmbed;
  const [bookingModalOpened, setBookingModalOpened] = useState(false);
  const [selectedCoach, setSelectedCoach] = useState("");
  const [selectedCoachFreeMeeting, setSelectedCoachFreeMeeting] = useState("");
  const router = useRouter();
  const { t } = useLocale();
  const { data: session } = useSession();
  const { data } = trpc.viewer.coaches.search.useQuery(
    {
      filters: { limit: 30 },
    },
    {
      keepPreviousData: true,
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
    }
  );

  const sliderOptions = useMemo(
    () => ({
      perView: 1,
      gap: 0,
      focusAt: "center" as const,
    }),
    []
  );

  const userId = session?.user.id ?? 0;
  const coaches = useMemo(() => {
    const score = (coachId: number) => (coachId * (userId + 1)) % 97;
    return [...(data?.results ?? [])].sort((a, b) => score(a.uid) - score(b.uid)).slice(0, VISIBLE_COACHES);
  }, [data, userId]);

  return (
    <div className="w-full">
      {!data ? <Loader /> : null}

      {!data || data.hasMatches ? null : (
        <div className="mb-6 flex w-full flex-col items-center space-y-3 rounded-md border border-dashed p-3 sm:flex-row sm:space-x-3 sm:space-y-0">
          <div className="bg-brand-100 flex h-12 w-12 shrink-0 items-center justify-center rounded-full dark:bg-white">
            <FiSearch className="text-brand-500 inline-block h-6 w-6 stroke-[1.3px] dark:bg-gray-900 dark:text-gray-600" />
          </div>
          <div className="flex flex-col items-center sm:items-start">
            <h2 className="text-semibold font-cal text-center dark:text-gray-300">
              {t("lb_search_coach_empty_title")}
            </h2>
            <div className="text-center text-sm font-normal leading-6 text-gray-700 sm:text-start dark:text-gray-300">
              {t("lb_search_coach_empty_subtitle")}
            </div>
          </div>
        </div>
      )}

      {coaches.length > 0 ? (
        <Slider
          noArrows={true}
          className={isEmbed ? "-mx-6 !mb-0 !mt-0 [&>div>div]:items-start" : ""}
          items={coaches}
          itemKey={(item) => item.uid}
          options={sliderOptions}
          renderItem={({ uid, username, ...profile }) => (
            <button
              key={uid}
              className={cn(
                "isolate mx-auto flex max-w-[85%] flex-col overflow-clip rounded-2xl bg-white px-2 pt-2 transition-colors hover:bg-gray-100"
              )}
              onClick={() => {
                setSelectedCoach(username);
                setSelectedCoachFreeMeeting(profile.slug);
                setBookingModalOpened(true);
                router.push(
                  {
                    query: {
                      ...router.query,
                      coach: username,
                    },
                  },
                  undefined,
                  { shallow: true }
                );
              }}>
              <img
                className="mx-auto h-32 w-32 rounded-lg object-cover"
                src={profile.avatar ?? ""}
                alt={profile?.firstName ?? ""}
              />
              <div className={cn("flex flex-col gap-1", isEmbed ? "p-2" : "p-6")}>
                <h3 className={cn("font-medium text-gray-900", isEmbed ? "text-sm" : "")}>
                  {profile?.firstName ?? ""} {profile?.lastName ?? ""}
                </h3>
                {/* <CoachRating coachUserId={uid} /> */}
                <div
                  className={cn(
                    "dark:text-darkgray-600 line-clamp-3 text-gray-500 [&_a]:text-blue-500 [&_a]:underline [&_a]:hover:text-blue-600",
                    isEmbed ? "text-xs" : "text-sm"
                  )}>
                  {profile.summary}
                </div>
              </div>
            </button>
          )}
        />
      ) : null}

      {!isEmbed && (
        <div className="mt-8 w-full px-4 sm:px-0">
          <Button
            className="flex w-full items-center justify-center text-base "
            size="lg"
            color="ghost"
            onClick={() => router.push("/search")}>
            {t("lb_choose_later")}
          </Button>
        </div>
      )}

      <MeetingBookingModal
        isOpen={bookingModalOpened}
        onClose={() => setBookingModalOpened(false)}
        slug={selectedCoachFreeMeeting}
        username={selectedCoach}
      />
    </div>
  );
};
